import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Phone,
  AccountBalance,
  AttachMoney
} from '@mui/icons-material';
import api from '../../services/api';
import DialogCloseButton from './DialogCloseButton';
import { brand, border } from '../../theme/colors';

const PAYMENT_METHODS = {
  'ecocash': { name: 'EcoCash', icon: <Phone /> },
  'onemoney': { name: 'OneMoney', icon: <Phone /> },
  'bank_transfer': { name: 'Bank Transfer', icon: <AccountBalance /> },
  'mukuru': { name: 'Mukuru', icon: <AttachMoney /> },
  'worldremit': { name: 'WorldRemit', icon: <AttachMoney /> }
};

const INITIAL_FORM = {
  amount: '',
  payment_method: 'ecocash',
  account_details: {
    phone: '',
    account_name: '',
    account_number: '',
    bank_name: '',
    recipient_name: '',
    recipient_phone: '',
    country: '',
    payout_branch: ''
  },
  notes: ''
};

const formatCurrency = (amount) => {
  return `$${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Shared withdrawal dialog used by both contractor and verifier financial views.
 *
 * Props:
 *   open             - boolean controlling dialog visibility
 *   onClose          - callback to close the dialog
 *   availableBalance - number, the user's withdrawable balance
 *   role             - 'contractor' | 'verifier'
 *   userId           - UUID of the current user
 *   onSuccess        - callback fired after a successful withdrawal request
 */
const WithdrawalDialog = ({ open, onClose, availableBalance, role, userId, onSuccess }) => {
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const resetForm = () => {
    setForm({ ...INITIAL_FORM, account_details: { ...INITIAL_FORM.account_details } });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'warning' });
      return;
    }

    if (parseFloat(form.amount) < 100) {
      setSnackbar({ open: true, message: 'Minimum withdrawal amount is $100', severity: 'warning' });
      return;
    }

    if (parseFloat(form.amount) > availableBalance) {
      setSnackbar({ open: true, message: 'Insufficient balance', severity: 'error' });
      return;
    }

    setSubmitting(true);

    try {
      const roleEndpoint = role === 'verifier' ? 'verifier' : 'contractor';
      await api.post(`/payments/${roleEndpoint}/${userId}/withdraw`, {
        amount: parseFloat(form.amount),
        paymentMethod: form.payment_method,
        paymentDetails: form.account_details,
        notes: form.notes
      });

      setSnackbar({ open: true, message: 'Withdrawal request submitted! Processing typically takes 1-3 business days.', severity: 'success' });
      resetForm();
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error submitting withdrawal:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to submit withdrawal request', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={handleClose} />
        <DialogTitle>Request Withdrawal</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Available balance: {formatCurrency(availableBalance)}
            </Alert>

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
              helperText="Minimum withdrawal: $100"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />

            <TextField
              fullWidth
              select
              label="Payment Method"
              value={form.payment_method}
              onChange={(e) => setForm(prev => ({ ...prev, payment_method: e.target.value }))}
              sx={{ mb: 2 }}
            >
              {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value.name}
                </MenuItem>
              ))}
            </TextField>

            {form.payment_method === 'ecocash' || form.payment_method === 'onemoney' ? (
              <TextField
                fullWidth
                label="Phone Number"
                value={form.account_details.phone}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  account_details: { ...prev.account_details, phone: e.target.value }
                }))}
                sx={{ mb: 2 }}
              />
            ) : form.payment_method === 'bank_transfer' ? (
              <>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={form.account_details.account_name}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, account_name: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Account Number"
                  value={form.account_details.account_number}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, account_number: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={form.account_details.bank_name}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, bank_name: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
              </>
            ) : (form.payment_method === 'mukuru' || form.payment_method === 'worldremit') ? (
              <>
                <TextField fullWidth label="Recipient Full Name" value={form.account_details.recipient_name}
                  onChange={(e) => setForm(prev => ({ ...prev, account_details: { ...prev.account_details, recipient_name: e.target.value } }))} sx={{ mb: 2 }} />
                <TextField fullWidth label="Recipient Phone" value={form.account_details.recipient_phone}
                  onChange={(e) => setForm(prev => ({ ...prev, account_details: { ...prev.account_details, recipient_phone: e.target.value } }))} sx={{ mb: 2 }} />
                <TextField fullWidth label="Destination Country" value={form.account_details.country}
                  onChange={(e) => setForm(prev => ({ ...prev, account_details: { ...prev.account_details, country: e.target.value } }))} sx={{ mb: 2 }} />
                <TextField fullWidth label="Payout Branch / Method" value={form.account_details.payout_branch}
                  onChange={(e) => setForm(prev => ({ ...prev, account_details: { ...prev.account_details, payout_branch: e.target.value } }))} sx={{ mb: 2 }} />
              </>
            ) : null}

            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              bgcolor: brand.primary,
              fontWeight: 700,
              '&:hover': { bgcolor: brand.primaryDark },
              '&:disabled': { bgcolor: border.light }
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WithdrawalDialog;
